// Bank Account services API (spec §2, §5). Mock-first; real endpoints replace
// these when the backend ships.
import { MOCK_BANK_SERVICES, REAL_API_ENABLED } from './flags';
import * as mock from '@/mock/handlers/bankServices';
import { ApiError, del, get, getBlob, patch, post, put } from './client';
import type {
  ChequeBook, ChequeLeaf, PositivePayEntry, RekycState, GstRegistration,
  FatcaRecord, Favourite, Dispute, Locker, LockerBranch, LockerSize, LockerWaitlist,
} from '@/types/phase4.types';

const unimplemented = (): never => {
  throw new Error('Bank services backend not available yet. This build runs on the mock (VITE_MOCK_BANK_SERVICES).');
};

export const accountFacts = (): { productType: string; pan: string } =>
  MOCK_BANK_SERVICES ? mock.accountFacts() : unimplemented();

export interface BankAccount {
  id: string;
  accountNumber: string;
  linkedId: number;
  customerId: string;
  accountType: string;
  branchName: string;
  ifsc: string;
  currency: string;
  status: string;
  availableBalance: number;
  ledgerBalance: number;
  lienAmount: number;
  holdAmount: number;
  minimumBalance: number;
  interestRate: number;
  referenceNumber: string;
  openingDate: string;
  activationDate: string;
  dormantFlag: boolean;
  freezeFlag: boolean;
  nickname: string;
  primaryAccount: boolean;
  closeDate: string;
  updatedAt: string;
}

export interface UpdateBankAccountInput {
  branchCode?: string;
  minimumBalance?: number;
  interestRate?: number;
  remarks?: string;
}

export interface AccountNicknameInput {
  nickname: string;
}

export interface AccountActionInput {
  reason: string;
  remarks?: string;
}

const accountsPath = (accountNumber: string): string =>
  `/accounts/${encodeURIComponent(accountNumber)}`;

const accountsUnavailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'The bank accounts API is not available.');
};

export const getBankAccount = (accountNumber: string): Promise<BankAccount> =>
  REAL_API_ENABLED
    ? get<BankAccount>(accountsPath(accountNumber))
    : accountsUnavailable();

export const setPrimaryBankAccount = (accountNumber: string): Promise<BankAccount> =>
  REAL_API_ENABLED
    ? post<BankAccount>(`${accountsPath(accountNumber)}/primary`)
    : accountsUnavailable();

export const updateBankAccount = (
  accountNumber: string,
  input: UpdateBankAccountInput,
): Promise<BankAccount> =>
  REAL_API_ENABLED
    ? patch<BankAccount>(accountsPath(accountNumber), input)
    : accountsUnavailable();

export const unfreezeBankAccount = (accountNumber: string): Promise<BankAccount> =>
  REAL_API_ENABLED
    ? patch<BankAccount>(`${accountsPath(accountNumber)}/unfreeze`)
    : accountsUnavailable();

export const setBankAccountNickname = (
  accountNumber: string,
  input: AccountNicknameInput,
): Promise<BankAccount> =>
  REAL_API_ENABLED
    ? patch<BankAccount>(`${accountsPath(accountNumber)}/nickname`, input)
    : accountsUnavailable();

export const freezeBankAccount = (
  accountNumber: string,
  input: AccountActionInput,
): Promise<BankAccount> =>
  REAL_API_ENABLED
    ? patch<BankAccount>(`${accountsPath(accountNumber)}/freeze`, input)
    : accountsUnavailable();

export const closeBankAccount = (
  accountNumber: string,
  input: AccountActionInput,
): Promise<BankAccount> =>
  REAL_API_ENABLED
    ? patch<BankAccount>(`${accountsPath(accountNumber)}/close`, input)
    : accountsUnavailable();

export interface AccountBalance {
  accountNumber: string;
  currency: string;
  availableBalance: number;
  ledgerBalance: number;
  lienAmount: number;
  holdAmount: number;
  minimumBalance: number;
  updatedAt: string;
}

export interface CustomerAccounts {
  customerId: string;
  totalAccounts: number;
  accounts: BankAccount[];
}

export const downloadAccountDepositSlip = (linkedId: number): Promise<Blob> =>
  REAL_API_ENABLED
    ? getBlob(`/accounts/${encodeURIComponent(linkedId)}/deposit-slip`)
    : accountsUnavailable();

export const getAccountNominee = (accountNumber: string): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? get<Record<string, unknown>>(`${accountsPath(accountNumber)}/nominee`)
    : accountsUnavailable();

export const getAccountFacts = (accountNumber: string): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? get<Record<string, unknown>>(`${accountsPath(accountNumber)}/facts`)
    : accountsUnavailable();

export const getAccountBalance = (accountNumber: string): Promise<AccountBalance> =>
  REAL_API_ENABLED
    ? get<AccountBalance>(`${accountsPath(accountNumber)}/balance`)
    : accountsUnavailable();

export const listCustomerAccounts = (customerId: string): Promise<CustomerAccounts> =>
  REAL_API_ENABLED
    ? get<CustomerAccounts>(`/accounts/customer/${encodeURIComponent(customerId)}`)
    : accountsUnavailable();

// debit cards
export type CardIssueKind = 'VIRTUAL_ONLY' | 'PHYSICAL_PLUS_VIRTUAL';

export interface DebitCardStatement {
  transactions: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface DebitCardDelivery {
  status: string;
  [key: string]: unknown;
}

export interface DebitCardVariant {
  issueKind: CardIssueKind;
  [key: string]: unknown;
}

const cardsPath = (cardId: string): string => `/cards/${encodeURIComponent(cardId)}`;

export const getDebitCardStatement = (cardId: string, month?: string): Promise<DebitCardStatement> =>
  REAL_API_ENABLED
    ? get<DebitCardStatement>(`${cardsPath(cardId)}/statement`, { params: month ? { month } : undefined })
    : accountsUnavailable();

export const getDebitCardDelivery = (cardId: string): Promise<DebitCardDelivery> =>
  REAL_API_ENABLED
    ? get<DebitCardDelivery>(`${cardsPath(cardId)}/delivery`)
    : accountsUnavailable();

export const listDebitCardVariants = (): Promise<DebitCardVariant[]> =>
  REAL_API_ENABLED
    ? get<DebitCardVariant[]>('/cards/variants')
    : accountsUnavailable();

export type DebitCardChannel =
  | 'DOMESTIC_ATM'
  | 'DOMESTIC_POS'
  | 'DOMESTIC_ONLINE'
  | 'CONTACTLESS'
  | 'INTERNATIONAL_ATM'
  | 'INTERNATIONAL_POS'
  | 'INTERNATIONAL_ONLINE';

export interface DebitCard {
  id: string;
  maskedNumber: string;
  variantName: string;
  issueKind: CardIssueKind | string;
  holderName: string;
  expiryDate: string;
  status: string;
  branchPickup: boolean;
  [key: string]: unknown;
}

export interface DebitCardChannelInput {
  enabled: boolean;
  limitPaise: number;
}

export interface CreateDebitCardInput extends Record<string, unknown> {
  variantId?: string;
  issueKind?: CardIssueKind;
  branchPickup?: boolean;
}

export interface DebitCardTravelNoticeInput extends Record<string, unknown> {
  startDate: string;
  endDate: string;
  countries: string[];
}

export interface DebitCardReissueInput extends Record<string, unknown> {
  reason: string;
}

export interface DebitCardPinInput extends Record<string, unknown> {
  pin: string;
}

export interface DebitCardBlockInput extends Record<string, unknown> {
  reason: string;
}

export interface DebitCardActivationInput extends Record<string, unknown> {
  activationCode?: string;
}

export interface DebitCardOtpVerificationInput extends Record<string, unknown> {
  otp: string;
}

export interface DebitCardToken {
  id: string;
  token: string;
  status: string;
  [key: string]: unknown;
}

export interface DebitCardOtpVerificationResult {
  verified: boolean;
  [key: string]: unknown;
}

export const updateDebitCardChannel = (
  cardId: string,
  channel: DebitCardChannel,
  input: DebitCardChannelInput,
): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? put<DebitCard>(
        `${cardsPath(cardId)}/channels/${encodeURIComponent(channel)}`,
        input,
      )
    : accountsUnavailable();

export const listDebitCards = (): Promise<DebitCard[]> =>
  REAL_API_ENABLED ? get<DebitCard[]>('/cards') : accountsUnavailable();

export const createDebitCard = (input: CreateDebitCardInput): Promise<DebitCard> =>
  REAL_API_ENABLED ? post<DebitCard>('/cards', input) : accountsUnavailable();

export const unlockDebitCard = (cardId: string): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? post<DebitCard>(`${cardsPath(cardId)}/unlock`)
    : accountsUnavailable();

export const addDebitCardTravelNotice = (
  cardId: string,
  input: DebitCardTravelNoticeInput,
): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? post<DebitCard>(`${cardsPath(cardId)}/travel`, input)
    : accountsUnavailable();

export const revealDebitCard = (cardId: string): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? post<DebitCard>(`${cardsPath(cardId)}/reveal`)
    : accountsUnavailable();

export const reissueDebitCard = (
  cardId: string,
  input: DebitCardReissueInput,
): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? post<DebitCard>(`${cardsPath(cardId)}/reissue`, input)
    : accountsUnavailable();

export const setDebitCardPin = (
  cardId: string,
  input: DebitCardPinInput,
): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? post<DebitCard>(`${cardsPath(cardId)}/pin`, input)
    : accountsUnavailable();

export const lockDebitCard = (cardId: string): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? post<DebitCard>(`${cardsPath(cardId)}/lock`)
    : accountsUnavailable();

export const blockDebitCard = (
  cardId: string,
  input: DebitCardBlockInput,
): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? post<DebitCard>(`${cardsPath(cardId)}/block`, input)
    : accountsUnavailable();

export const activateDebitCard = (
  cardId: string,
  input: DebitCardActivationInput = {},
): Promise<DebitCard> =>
  REAL_API_ENABLED
    ? post<DebitCard>(`${cardsPath(cardId)}/activate`, input)
    : accountsUnavailable();

export const verifyDebitCardOtp = (
  input: DebitCardOtpVerificationInput,
): Promise<DebitCardOtpVerificationResult> =>
  REAL_API_ENABLED
    ? post<DebitCardOtpVerificationResult>('/cards/otp/verify', input)
    : accountsUnavailable();

export const getDebitCard = (cardId: string): Promise<DebitCard> =>
  REAL_API_ENABLED ? get<DebitCard>(cardsPath(cardId)) : accountsUnavailable();

export const listDebitCardTokens = (cardId: string): Promise<DebitCardToken[]> =>
  REAL_API_ENABLED
    ? get<DebitCardToken[]>(`${cardsPath(cardId)}/tokens`)
    : accountsUnavailable();

export const deleteAllDebitCardTokens = (cardId: string): Promise<void> =>
  REAL_API_ENABLED
    ? del<void>(`${cardsPath(cardId)}/tokens`)
    : accountsUnavailable();

export const deleteDebitCardToken = (
  cardId: string,
  tokenId: string,
): Promise<void> =>
  REAL_API_ENABLED
    ? del<void>(
        `${cardsPath(cardId)}/tokens/${encodeURIComponent(tokenId)}`,
      )
    : accountsUnavailable();

// cheques
export const chequeBooks = (): Promise<ChequeBook[]> => (MOCK_BANK_SERVICES ? mock.chequeBooks() : unimplemented());
export const chequeLeaves = (bookId: string): Promise<ChequeLeaf[]> => (MOCK_BANK_SERVICES ? mock.chequeLeaves(bookId) : unimplemented());
export const chequeBookQuote = (leaves: number) => (MOCK_BANK_SERVICES ? mock.chequeBookQuote(leaves) : unimplemented());
export const requestChequeBook = (leaves: number): Promise<ChequeBook> => (MOCK_BANK_SERVICES ? mock.requestChequeBook(leaves) : unimplemented());
export const stopCheques = (input: Parameters<typeof mock.stopCheques>[0]) => (MOCK_BANK_SERVICES ? mock.stopCheques(input) : unimplemented());
export const revokeStop = (n: number): Promise<void> => (MOCK_BANK_SERVICES ? mock.revokeStop(n) : unimplemented());
export const positivePay = (): Promise<PositivePayEntry[]> => (MOCK_BANK_SERVICES ? mock.positivePay() : unimplemented());
export const addPositivePay = (input: Parameters<typeof mock.addPositivePay>[0]) => (MOCK_BANK_SERVICES ? mock.addPositivePay(input) : unimplemented());
export const cancelPositivePay = (id: string): Promise<void> =>
  MOCK_BANK_SERVICES ? mock.cancelPositivePay(id) : unimplemented();

export interface ChequeControllerInput {
  accountNumber: string;
  chequeNumber: string;
  reason: string;
}

export interface ChequeBookControllerInput {
  accountNumber: string;
  leaves: number;
}

export type ChequeControllerRecord = Record<string, unknown>;
export type ChequeControllerList = ChequeControllerRecord[];

export const stopCheque = (
  input: ChequeControllerInput,
): Promise<ChequeControllerRecord> =>
  REAL_API_ENABLED
    ? post<ChequeControllerRecord>('/cheques/stop', input)
    : unimplemented();

export const registerPositivePayCheque = (
  input: ChequeControllerInput,
): Promise<ChequeControllerRecord> =>
  REAL_API_ENABLED
    ? post<ChequeControllerRecord>('/cheques/positive-pay', input)
    : unimplemented();

export const requestChequeBookController = (
  input: ChequeBookControllerInput,
): Promise<ChequeControllerRecord> =>
  REAL_API_ENABLED
    ? post<ChequeControllerRecord>('/cheques/book', input)
    : unimplemented();

export const listCheques = (): Promise<ChequeControllerList> =>
  REAL_API_ENABLED ? get<ChequeControllerList>('/cheques') : unimplemented();

export const getCheque = (id: string): Promise<ChequeControllerRecord> =>
  REAL_API_ENABLED
    ? get<ChequeControllerRecord>(`/cheques/${encodeURIComponent(id)}`)
    : unimplemented();

// re-KYC
export interface RekycControllerResult {
  dueDate: string;
  status: string;
  lastDoneAt: string;
  blocksDebits: boolean;
  [key: string]: unknown;
}

export interface RekycAddressChangeInput {
  addressType?: string;
  houseNumber?: string;
  building?: string;
  street: string;
  area: string;
  landmark?: string;
  village?: string;
  locality?: string;
  city: string;
  district?: string;
  state: string;
  country?: string;
  pinCode?: string;
  addressProofType?: string;
  communicationAddress?: boolean;
  house?: string;
  pin?: string;
}

type RekycViewState = RekycState & {
  restrictsDebits: boolean;
  usableUntil?: number;
  status: 'UP_TO_DATE' | 'DUE' | 'OVERDUE';
};

const toRekycViewState = (result: RekycControllerResult): RekycViewState => ({
  risk: 'MEDIUM',
  lastUpdatedAt: Date.parse(result.lastDoneAt),
  dueAt: Date.parse(result.dueDate),
  remindersSent: [],
  restrictsDebits: result.blocksDebits,
  status: result.status as RekycViewState['status'],
});

export const rekyc = (): Promise<RekycViewState> => {
  if (REAL_API_ENABLED) {
    return get<RekycControllerResult>('/customers/me/rekyc').then(toRekycViewState);
  }
  return MOCK_BANK_SERVICES ? mock.rekyc() : unimplemented();
};

export const rekycNoChange = (): Promise<void> => {
  if (REAL_API_ENABLED) {
    return post<RekycControllerResult>('/customers/me/rekyc/no-change').then(() => undefined);
  }
  return MOCK_BANK_SERVICES ? mock.rekycNoChange() : unimplemented();
};

export const rekycAddressChange = (input: RekycAddressChangeInput): Promise<void> => {
  if (REAL_API_ENABLED) {
    const request = {
      addressType: input.addressType ?? 'CURRENT',
      houseNumber: input.houseNumber ?? input.house ?? '',
      building: input.building ?? '',
      street: input.street,
      area: input.area,
      landmark: input.landmark ?? '',
      village: input.village ?? '',
      locality: input.locality ?? '',
      city: input.city,
      district: input.district ?? '',
      state: input.state,
      country: input.country ?? 'INDIA',
      pinCode: input.pinCode ?? input.pin ?? '',
      addressProofType: input.addressProofType ?? 'AADHAAR',
      communicationAddress: input.communicationAddress ?? true,
    };
    return post<RekycControllerResult>('/customers/me/rekyc/address-change', request)
      .then(() => undefined);
  }
  return MOCK_BANK_SERVICES
    ? mock.rekycAddressChange({
        house: input.house ?? input.houseNumber ?? '',
        street: input.street,
        area: input.area,
        pin: input.pin ?? input.pinCode ?? '',
        city: input.city,
        state: input.state,
      })
    : unimplemented();
};
export const lookupPin = (pin: string) => (MOCK_BANK_SERVICES ? mock.lookupPin(pin) : unimplemented());

// GST
export const gstRegistrations = (): Promise<GstRegistration[]> => (MOCK_BANK_SERVICES ? mock.gstRegistrations() : unimplemented());
export const lookupGstin = (gstin: string) => (MOCK_BANK_SERVICES ? mock.lookupGstin(gstin) : unimplemented());
export const addGstin = (gstin: string): Promise<GstRegistration> => (MOCK_BANK_SERVICES ? mock.addGstin(gstin) : unimplemented());
export const removeGstin = (gstin: string): Promise<void> => (MOCK_BANK_SERVICES ? mock.removeGstin(gstin) : unimplemented());
export const setPrimaryGstin = (gstin: string): Promise<void> => (MOCK_BANK_SERVICES ? mock.setPrimaryGstin(gstin) : unimplemented());
export const gstInvoices = () => (MOCK_BANK_SERVICES ? mock.gstInvoices() : unimplemented());

// FATCA
export const fatca = (): Promise<FatcaRecord> => (MOCK_BANK_SERVICES ? mock.fatca() : unimplemented());
export const fatcaReview = () => (MOCK_BANK_SERVICES ? mock.fatcaReview() : unimplemented());
export const updateFatca = (input: Parameters<typeof mock.updateFatca>[0]) => (MOCK_BANK_SERVICES ? mock.updateFatca(input) : unimplemented());

// favourites
export const favourites = (): Promise<Favourite[]> => (MOCK_BANK_SERVICES ? mock.favourites() : unimplemented());
export const addFavourite = (input: Parameters<typeof mock.addFavourite>[0]) => (MOCK_BANK_SERVICES ? mock.addFavourite(input) : unimplemented());
export const renameFavourite = (id: string, label: string): Promise<void> => (MOCK_BANK_SERVICES ? mock.renameFavourite(id, label) : unimplemented());
export const removeFavourite = (id: string): Promise<void> => (MOCK_BANK_SERVICES ? mock.removeFavourite(id) : unimplemented());
export const reorderFavourites = (ids: string[]): Promise<void> => (MOCK_BANK_SERVICES ? mock.reorderFavourites(ids) : unimplemented());

// disputes & fraud
export const disputableDebits = (impsOnly: boolean) => (MOCK_BANK_SERVICES ? mock.disputableDebits(impsOnly) : unimplemented());
export const disputes = (): Promise<Dispute[]> => (MOCK_BANK_SERVICES ? mock.disputes() : unimplemented());
export const raiseDispute = (input: Parameters<typeof mock.raiseDispute>[0]) => (MOCK_BANK_SERVICES ? mock.raiseDispute(input) : unimplemented());
export const reportFraud = (reason: string) => (MOCK_BANK_SERVICES ? mock.reportFraud(reason) : unimplemented());

// lockers
export const lockerAvailability = (): Promise<LockerBranch[]> => (MOCK_BANK_SERVICES ? mock.lockerAvailability() : unimplemented());
export const lockers = (): Promise<{ lockers: Locker[]; waitlist: LockerWaitlist[] }> => (MOCK_BANK_SERVICES ? mock.lockers() : unimplemented());
export const joinLockerWaitlist = (branchId: string, size: LockerSize) => (MOCK_BANK_SERVICES ? mock.joinLockerWaitlist(branchId, size) : unimplemented());
export const applyForLocker = (input: Parameters<typeof mock.applyForLocker>[0]) => (MOCK_BANK_SERVICES ? mock.applyForLocker(input) : unimplemented());
export const setLockerAutoDebit = (id: string, on: boolean): Promise<void> => (MOCK_BANK_SERVICES ? mock.setLockerAutoDebit(id, on) : unimplemented());
export const recordLockerVisit = (id: string): Promise<void> => (MOCK_BANK_SERVICES ? mock.recordLockerVisit(id) : unimplemented());
export const surrenderLocker = (id: string) => (MOCK_BANK_SERVICES ? mock.surrenderLocker(id) : unimplemented());

// nominee (read-only)
export const accountNominee = () => (MOCK_BANK_SERVICES ? mock.accountNominee() : unimplemented());

export { LOCKER_SIZES } from '@/mock/handlers/bankServices';
